import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAfiliados } from '../../reducers/afiliado_slice';
import Table from 'react-bootstrap/Table';
import CustomPrintModal from '../UI/CustomPrintModal1';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { fetchSituaciones } from '../../reducers/situacion_slice';
import { apellidosNombreFullPersona, formatearFechaSimple } from '../../util/customFormatter';
import { fechaHoraActualBoliviaSoloVista } from '../../util/valueCalculator';

const AfiliadoRepoGral = () => {
  const dispatch = useDispatch();
  const afiliados = useSelector(state => state.afiliado.afiliados || []);
  const situaciones = useSelector(state => state.situacion.situaciones || []);

  const [selectedSituaciones, setSelectedSituaciones] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [customTitle, setCustomTitle] = useState('');

  useEffect(() => {
    dispatch(fetchAfiliados());
    dispatch(fetchSituaciones());
  }, [dispatch]);

  useEffect(() => {
    if (situaciones && situaciones.length > 0) {
      const situSelectedDefect = [...situaciones]
        .filter((situ) => situ.alias === "activo")
        .map(situ => ({ value: situ.id, label: situ.nombre }));
      setSelectedSituaciones(situSelectedDefect);
    }
  }, [situaciones]);

  const handleFilterSituacionChange = selected => {
    setSelectedSituaciones(selected || []);
  };

  // Filtrar afiliados solo por situación
  const filteredAfiliados = useMemo(() => {
    let result = [...afiliados];

    if (selectedSituaciones.length > 0) {
      result = result.filter(afiliado => 
        selectedSituaciones.some(situ => situ.value === afiliado.id_situacion)
      );
    }

    return result;
  }, [afiliados, selectedSituaciones]);

  // Ordenar afiliados
  const sortedAfiliados = useMemo(() => {
    const sorted = [...filteredAfiliados];
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key === 'num_reg') {
        aValue = parseInt(a.num_reg, 10);
        bValue = parseInt(b.num_reg, 10);
      } else if (sortConfig.key === 'residencia') {
        aValue = (a.lugar_residencia?.nombre || '').toLowerCase();
        bValue = (b.lugar_residencia?.nombre || '').toLowerCase();
      } else {
        aValue = `${apellidosNombreFullPersona(a.persona)}`.toLowerCase();
        bValue = `${apellidosNombreFullPersona(b.persona)}`.toLowerCase();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredAfiliados, sortConfig]);

  const handleSort = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleExport = () => {
    const exportData = sortedAfiliados.map((afiliado, index) => {
      const nombreCompleto = apellidosNombreFullPersona(afiliado.persona);
      
      return {
        '#': index + 1,
        'Nombre Completo': nombreCompleto,
        'Número Registro': afiliado.num_reg,
        'C.I.': afiliado.persona?.ci || '',
        'Fecha Registro C.A.B.': formatearFechaSimple(afiliado.fecha_reg_cab),
        'Fecha Nacimiento': formatearFechaSimple(afiliado.persona?.fecha_nac),
        'Residencia': afiliado.lugar_residencia?.nombre || '',
        'Situación': afiliado.situacion?.nombre || 'N/A',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Afiliados');
    XLSX.writeFile(workbook, `afiliados_general.xlsx`);
  };

  const getPreviewContent = () => {
    const headRow = ['#', 'Nombre Completo', 'Número Registro', 'C.I.', 'Fecha Registro C.A.B.', 'Fecha Nacimiento', 'Residencia', 'Situación'];

    const bodyContent = sortedAfiliados.map((afiliado, index) => {
      return [
        index + 1,
        apellidosNombreFullPersona(afiliado.persona),
        afiliado.num_reg,
        afiliado.persona?.ci || '',
        formatearFechaSimple(afiliado.fecha_reg_cab),
        formatearFechaSimple(afiliado.persona?.fecha_nac),
        afiliado.lugar_residencia?.nombre || '',
        afiliado.situacion?.nombre || 'N/A',
      ];
    });

    return {
      head: [headRow],
      body: bodyContent,
      title2: customTitle || 'REPORTE GENERAL DE SOCIOS',
      title3: `Total de registros: ${sortedAfiliados.length}`,
    };
  };

  const situacionesSelect = situaciones
    .filter(s => s.alias && !s.alias.includes("preinscrito"))
    .map(situ => ({ value: situ.id, label: situ.nombre }));

  return (
    <div className="list-container list1">
      <h1 className="table-title">REPORTE GENERAL DE SOCIOS</h1>
      <div className="list-content">
        <div className='row mb-3'>
          <div className='col-md-4'>
            <label className="form-label">Situación</label>
            <Select
              isMulti
              options={situacionesSelect}
              value={selectedSituaciones}
              onChange={handleFilterSituacionChange}
              placeholder="Filtrar por situación..."
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Título del reporte</label>
            <input
              type="text"
              className="form-control"
              placeholder="Título personalizado"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end mb-3 gap-2">
          <button
            title="Imprimir"
            onClick={() => setShowPreviewModal(true)}
            className="btn btn-sm btn-primary"
          >
            <i className="bi bi-printer"></i> Imprimir
          </button>

          <button
            title="Exportar a Excel"
            onClick={handleExport}
            className="btn btn-sm btn-success"
          >
            <i className="bi bi-file-spreadsheet"></i> Exportar
          </button>
        </div>

        {sortedAfiliados.length === 0 ? (
          <p>No hay afiliados disponibles.</p>
        ) : (
          <div className="table-container">
            <Table bordered hover size="sm">
              <thead style={{ backgroundColor: '#b2aea3' }}>
                <tr>
                  <th>#</th>
                  <th onClick={() => handleSort('nombre_completo')} style={{ cursor: 'pointer' }}>
                    Nombre Completo {sortConfig.key === 'nombre_completo' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('num_reg')} style={{ cursor: 'pointer' }}>
                    Número Registro {sortConfig.key === 'num_reg' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>C.I.</th>
                  <th>Fecha Registro C.A.B.</th>
                  <th>Fecha Nacimiento</th>
                  <th onClick={() => handleSort('residencia')} style={{ cursor: 'pointer' }}>
                    Residencia {sortConfig.key === 'residencia' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Situación</th>
                </tr>
              </thead>
              <tbody>
                {sortedAfiliados.map((afiliado, index) => (
                  <tr key={afiliado.id}>
                    <td>{index + 1}</td>
                    <td>{apellidosNombreFullPersona(afiliado.persona)}</td>
                    <td>{afiliado.num_reg}</td>
                    <td>{afiliado.persona?.ci}</td>
                    <td>{formatearFechaSimple(afiliado.fecha_reg_cab)}</td>
                    <td>{formatearFechaSimple(afiliado.persona?.fecha_nac)}</td>
                    <td>{afiliado.lugar_residencia?.nombre || ''}</td>
                    <td>{afiliado.situacion?.nombre || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {showPreviewModal && (
          <CustomPrintModal
            show={showPreviewModal}
            handleClose={() => setShowPreviewModal(false)}
            title1={`REPORTE GENERAL DE SOCIOS${customTitle && `\n${customTitle}`}`}
            title2={`${selectedSituaciones.length === 0 ? "Todas las situaciones" : `Situación: ${selectedSituaciones.map(s => s.label).join(", ")}`}`}
            title3={`Fecha de consulta: ${fechaHoraActualBoliviaSoloVista("soloFecha")} / Resultado: ${sortedAfiliados.length} registros`}
            content={getPreviewContent()}
          />
        )}
      </div>
    </div>
  );
};

export default AfiliadoRepoGral;